'use client';
import { API_BASES } from '../../config/constants';
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Box, Typography, CircularProgress, IconButton, Paper, List, ListItem, ListItemText, Button, Stack, Tooltip } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';

import { baseMaps } from '../../utils/mapConfig';
import { generatePathAndSpeeds } from '../../utils/mapTripUtils';
import { timezone } from '../../utils/timeZone';

import TripHistoryListForShare from './content/TripHistoryListForShare';
import BottomPopoverTripHistoryForShare from './content/BottomPopoverTripHistoryForShare';
import MapSidebar from '../map-tracking/side-map/MapSidebar';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LayersIcon from '@mui/icons-material/Layers';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import CloseIcon from '@mui/icons-material/Close';
import SpeedIcon from '@mui/icons-material/Speed';

import TrackingTruck from 'assets/images/icons/tracking-truck.webp';
import EngineIdleMarker from 'assets/images/icons/engineIdle-marker.webp';
import EngineOffMarker from 'assets/images/icons/engineOff-marker.webp';
import EngineOverMarker from 'assets/images/icons/engineOver-marker.webp';
const SENSOR_CONFIG = {
  DRB: { label: 'Brake (DRB)', color: '#F44336' },
  DRL: { label: 'Turn Left (DRL)', color: '#4CAF50' },
  DRR: { label: 'Turn Right (DRR)', color: '#2196F3' },
  DUM: { label: 'Dump (DUM)', color: '#FF9800' },
  DPB: { label: 'DPB', color: '#9C27B0' },
  OFF: { label: 'Engine OFF', color: '#9E9E9E' },
  '': { label: 'Normal Route', color: '#1d89d5' }
};

const SENSOR_TYPES = ['DRB', 'DRL', 'DRR', 'DUM', 'DPB', 'OFF', ''];

const extractSensorType = (wp) => String(wp.sensor_type ?? wp.sensor ?? wp.event ?? wp.type ?? '').toUpperCase();

const enrichWaypointsWithSensorData = (waypoints, tripList = []) => {
  const effectiveTrips = (tripList || []).map((trip) => {
    if (!trip.start_time || !trip.end_time) return { sTime: 0, loadedEndTime: 0 };

    const sTime = trip.start_time ? dayjs(trip.start_time).valueOf() : 0;
    const eTime = trip.end_time ? dayjs(trip.end_time).valueOf() : 0;

    const routes = trip.routes || [];
    const spatialNames = routes.map((v) => (v.spatial_name || v.spatialName || '').toLowerCase().trim()).filter(Boolean);
    const destSpatialName = spatialNames.length > 0 ? spatialNames[spatialNames.length - 1] : null;

    let loadedEndTime = eTime;

    if (destSpatialName) {
      for (let i = 0; i < waypoints.length; i++) {
        const wp = waypoints[i];
        const wpEpoch = getEpochFromWp(wp);

        if (wpEpoch >= sTime && wpEpoch <= eTime) {
          const wpSpatialName = String(
            wp.spatial_description ||
              wp.info?.spatial_description ||
              wp.polygon_type_name ||
              wp.info?.polygon_type_name ||
              wp.spatial_name ||
              wp.info?.spatial_name ||
              ''
          )
            .toLowerCase()
            .trim();

          const isAtDest =
            wpSpatialName && destSpatialName && (wpSpatialName === destSpatialName || wpSpatialName.includes(destSpatialName));

          if (isAtDest) {
            loadedEndTime = wpEpoch;
            break;
          }
        }
      }
    }
    return { sTime, loadedEndTime };
  });

  return waypoints.map((wp) => {
    let is_in_retase = false;
    const wpEpoch = getEpochFromWp(wp);

    if (effectiveTrips.length > 0) {
      for (const trip of effectiveTrips) {
        if (trip.sTime > 0 && wpEpoch >= trip.sTime && wpEpoch <= trip.loadedEndTime) {
          is_in_retase = true;
          break;
        }
      }
    }

    let modifiedWp = { ...wp, is_in_retase };
    if (!is_in_retase) {
      if (modifiedWp.spatial_max_speed) modifiedWp.spatial_max_speed = 0;
      if (modifiedWp.info?.spatial_max_speed) modifiedWp.info = { ...modifiedWp.info, spatial_max_speed: 0 };
    }

    const sType = extractSensorType(modifiedWp);
    const validConfigKey = SENSOR_CONFIG[sType] ? sType : '';
    const config = SENSOR_CONFIG[validConfigKey];
    return { ...modifiedWp, _computedSensorType: validConfigKey, sensorLabel: config.label, lineColor: config.color };
  });
};

const getEpochFromWp = (wp) => {
  if (!wp) return 0;

  // Prioritaskan start_wp_time sesuai permintaan backend
  let dateStr = wp.start_wp_time ?? wp.start_time ?? wp.startTime;
  if (typeof dateStr === 'string' && dateStr) {
    dateStr = dateStr.replace(' ', 'T');
    const parsed = Date.parse(dateStr);
    if (!isNaN(parsed)) return parsed;
  }

  const epoch = wp.start_wp_time_epoch ?? wp.start_time ?? wp.end_time;
  if (typeof epoch === 'number') {
    if (epoch < 100000000000) return epoch * 1000;
    return epoch;
  }

  return 0;
};

const calculateHeading = (lat1, lon1, lat2, lon2) => {
  const toRad = (degree) => (degree * Math.PI) / 180;
  const toDeg = (rad) => (rad * 180) / Math.PI;
  const φ1 = toRad(lat1),
    φ2 = toRad(lat2),
    Δλ = toRad(lon2 - lon1);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

const formatDisplayTime = (isoString) => {
  if (!isoString) return '-';
  try {
    let val = isoString;
    if (typeof val === 'string' && /^\d+$/.test(val)) {
      val = parseInt(val, 10);
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return isoString;
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch {
    return isoString;
  }
};

const getRotatedTruckIcon = (angle) => {
  const rotation = Number(angle || 0) - 90;
  const truckSrc = typeof TrackingTruck === 'string' ? TrackingTruck : TrackingTruck?.src || TrackingTruck;
  return L.divIcon({
    className: 'custom-rotated-truck',
    html: `<div style="width: 100%; height: 100%; display: flex; justify-content: center; align-items: center; position: relative;">
             <div id="trip-vehicle-pulse" class="pulse-ring"></div>
             <img id="trip-vehicle-img" src="${truckSrc}" style="width: 60px; height: auto; transform: rotate(${rotation}deg); transform-origin: center center; transition: transform 0.2s linear; drop-shadow: 0 4px 6px rgba(0,0,0,0.3); z-index: 2; position: relative;" />
           </div>`,
    iconSize: [60, 60],
    iconAnchor: [30, 30]
  });
};

const isPointInPolygon = (latlng, poly) => {
  if (!poly || typeof poly.getLatLngs !== 'function') return false;
  const lat = latlng[0];
  const lng = latlng[1];
  const coords = poly.getLatLngs()[0];

  let isInside = false;
  for (let i = 0, j = coords.length - 1; i < coords.length; j = i++) {
    const xi = coords[i].lat,
      yi = coords[i].lng;
    const xj = coords[j].lat,
      yj = coords[j].lng;
    const intersect = yi > lng !== yj > lng && lat < ((xj - xi) * (lng - yi)) / (yj - yi) + xi;
    if (intersect) isInside = !isInside;
  }
  return isInside;
};

// ================= FETCH SERVICES =================

const splitDateRangeIntoDays = (startStr, endStr) => {
  const ranges = [];
  let currentStart = dayjs(startStr);
  const end = dayjs(endStr);

  while (currentStart.isBefore(end) || currentStart.isSame(end)) {
    let currentEnd = currentStart.endOf('day');
    if (currentEnd.isAfter(end)) {
      currentEnd = end;
    }
    ranges.push({
      start: currentStart.toISOString(),
      end: currentEnd.toISOString()
    });
    currentStart = currentStart.add(1, 'day').startOf('day');
  }
  return ranges;
};

const formatIsoClean = (dateStr) => {
  if (!dateStr) return '';
  const cleanStr = dateStr.split('.')[0];
  return cleanStr.endsWith('Z') ? cleanStr : cleanStr + 'Z';
};

const fetchRetasePublic = async (token, vehicleId, companyId, startTime, endTime, tz = null) => {
  const tzVal = tz !== null ? tz : typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
  console.log('fetchRetasePublic dipanggil dengan:', { vehicleId, startTime, endTime, tz: tzVal });
  try {
    const dailyRanges = splitDateRangeIntoDays(startTime, endTime);
    let allData = [];

    for (const range of dailyRanges) {
      let urlStr = `${API_BASES.FLEET_API}/retase?vehicleId=${encodeURIComponent(vehicleId)}`;
      if (companyId) urlStr += `&companyId=${encodeURIComponent(companyId)}`;
      urlStr += `&tz=${tzVal}&startTime=${encodeURIComponent(formatIsoClean(range.start))}&endTime=${encodeURIComponent(formatIsoClean(range.end))}&isRouteSort=true`;

      console.log('Fetching retase URL:', urlStr);
      const response = await fetch(urlStr, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json, text/plain, */*',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        console.warn('Gagal menarik data Retase untuk range:', range);
        continue;
      }
      const json = await response.json();
      let batchData = [];
      if (json?.data?.retase && Array.isArray(json.data.retase)) {
        batchData = json.data.retase;
      } else if (Array.isArray(json?.data)) {
        batchData = json.data;
      } else if (Array.isArray(json)) {
        batchData = json;
      }
      allData = [...allData, ...batchData];
    }
    return { data: { retase: allData } };
  } catch (error) {
    console.error(error);
    return { data: { retase: [] } };
  }
};

const fetchWaypointsForType = async (token, vehicleId, companyId, startTime, endTime, singleType, tz = null) => {
  const tzVal = tz !== null ? tz : typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
  try {
    const dailyRanges = splitDateRangeIntoDays(startTime, endTime);
    let allData = [];

    for (const range of dailyRanges) {
      const pageSize = 1000;
      let start = 0;
      let hasMore = true;

      const payload = {
        columns: [{ logic_operator: 'RANGE', name: 'wp_time', operator: 'AND', value: range.start, value1: range.end }]
      };

      while (hasMore) {
        payload.paging = { start: start, length: pageSize };

        const url = new URL(`${API_BASES.FLEET_API}/history-waypoint/search`);
        url.searchParams.append('vehicleId', vehicleId);
        if (companyId) url.searchParams.append('companyId', companyId);
        url.searchParams.append('tz', String(tzVal));
        if (singleType) url.searchParams.append('type', singleType);

        const response = await fetch(url.toString(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json, text/plain, */*',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          console.warn(`API Waypoint Error for type ${singleType} in range`, range);
          break; // move to next day
        }
        const resJson = await response.json();

        const batchData = Array.isArray(resJson?.data) ? resJson.data : Array.isArray(resJson) ? resJson : [];
        if (batchData.length === 0) hasMore = false;
        else {
          allData = [...allData, ...batchData];
          if (batchData.length < pageSize) hasMore = false;
          else start += pageSize;
        }
        if (start > 50000) hasMore = false;
      }
    }
    return allData;
  } catch (err) {
    console.error(err);
    return [];
  }
};

const fetchAllWaypointsPublic = async (token, vehicleId, companyId, startTime, endTime, tz = null) => {
  try {
    const results = await Promise.all(
      SENSOR_TYPES.map((type) => fetchWaypointsForType(token, vehicleId, companyId, startTime, endTime, type, tz))
    );
    let combinedData = results.flat();
    combinedData.sort(
      (a, b) => new Date(a.start_wp_time || a.start_time || 0).getTime() - new Date(b.start_wp_time || b.start_time || 0).getTime()
    );
    return combinedData;
  } catch (err) {
    console.error('Error fetching multiple history types:', err);
    return [];
  }
};

const fetchPolygonData = async (endpoint, token, companyId, locationType) => {
  try {
    const url = `${API_BASES.FLEET_API}${endpoint}`;
    const columns = [];
    if (companyId) {
      const idStr = String(companyId);
      columns.push({ logic_operator: 'in', name: 'company_id', operator: idStr.includes(',') ? 'AND' : 'AND', value: idStr });
    }
    const payload = { paging: { start: 0, length: 1000 }, columns };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json, text/plain, */*',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`Failed to fetch ${locationType}`);
    const resJson = await response.json();
    const batchData = Array.isArray(resJson?.data) ? resJson.data : Array.isArray(resJson) ? resJson : [];
    return batchData.map((item) => ({ ...item, location_type: locationType }));
  } catch (error) {
    console.error(error);
    return [];
  }
};

const fetchAllLocationsPublic = async (token, companyId) => {
  try {
    const results = await Promise.all([
      fetchPolygonData('/pool/search', token, companyId, 'PIT'),
      fetchPolygonData('/transit-point/search', token, companyId, 'Jetty'),
      fetchPolygonData('/geofence/search', token, companyId, 'Geofence library')
    ]);
    return results.flat();
  } catch (error) {
    console.error('Error fetching all locations:', error);
    return [];
  }
};

export default function SharedTripHistoryPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const wrapperRef = useRef(null);
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isMapReady, setIsMapReady] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isExpired, setIsExpired] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLayerMenuOpen, setIsLayerMenuOpen] = useState(false);
  const [activeBaseMap, setActiveBaseMap] = useState('Satelite Map');

  const [payloadData, setPayloadData] = useState(null);
  const [tripList, setTripList] = useState([]);
  const [activePlayId, setActivePlayId] = useState(null);
  const [activeTimeRange, setActiveTimeRange] = useState({ start: 0, end: 0 });
  const [allWaypointsRaw, setAllWaypointsRaw] = useState([]);
  const [activeWaypoints, setActiveWaypoints] = useState([]);
  const [polygonLocations, setPolygonLocations] = useState([]);
  const [isPopoverOpen, setIsPopoverOpen] = useState(true);

  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(0.5);
  const [currentIndexState, setCurrentIndexState] = useState(0);
  const [currentPointSpeed, setCurrentPointSpeed] = useState(0);
  const [currentRPM, setCurrentRPM] = useState(0);
  const [currentFuel, setCurrentFuel] = useState({ litre: 0, percent: 0, cap: 100 });

  const isPlayingRef = useRef(false);
  const playbackSpeedRef = useRef(0.5);
  const lastTimeRef = useRef(0);

  const tripLayerRef = useRef(null);
  const historyClustersRef = useRef({ over: null, idle: null, off: null });
  const locationLayerRef = useRef(null);
  const tripMarkerRef = useRef(null);
  const startMarkerRef = useRef(null);
  const endMarkerRef = useRef(null);
  const animIndexRef = useRef(0);
  const eventMarkersRef = useRef({});
  const lastIndexRef = useRef(-1);
  const popupTimeoutRef = useRef(null);
  const currentAreaIdRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeLocationPolygon, setActiveLocationPolygon] = useState(null);
  const [locPopoverOpen, setLocPopoverOpen] = useState(false);
  const isSidebarOpenRef = useRef(isSidebarOpen);

  useEffect(() => {
    isSidebarOpenRef.current = isSidebarOpen;
  }, [isSidebarOpen]);

  useEffect(() => {
    playbackSpeedRef.current = playbackSpeed;
  }, [playbackSpeed]);

  const updatePlaying = useCallback((next) => {
    isPlayingRef.current = next;
    setIsPlaying(next);
    if (next) lastTimeRef.current = performance.now();
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      if (wrapperRef.current?.requestFullscreen) wrapperRef.current.requestFullscreen();
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      setTimeout(() => {
        if (mapInstanceRef.current) mapInstanceRef.current.invalidateSize();
      }, 1500);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);
  useEffect(() => {
    const initializeData = async () => {
      const searchParams = new URLSearchParams(location.search);
      const sharingCode = searchParams.get('data');

      if (!sharingCode) {
        setErrorMsg('Link trip history tidak valid.');
        setIsLoading(false);
        return;
      }

      try {
        let safeBase64 = sharingCode;
        const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;

        if (sharingCode.includes('-')) {
          const res = await fetch(`${API_BASES.FLEET_API}/share-link-bycode?tz=${tz}&sharingCode=${sharingCode}`);
          if (!res.ok) throw new Error('Sharing code tidak valid atau kadaluarsa');
          const jsonRes = await res.json();
          const encodeBase64 = jsonRes?.data?.encode_base64;
          if (!encodeBase64) throw new Error('Data encode_base64 tidak ditemukan');
          safeBase64 = encodeBase64;
        }

        safeBase64 = safeBase64.replace(/ /g, '+');
        safeBase64 = safeBase64.replace(/[^A-Za-z0-9+/=]/g, '');
        while (safeBase64.length % 4 !== 0) safeBase64 += '=';

        const decodedString = decodeURIComponent(escape(atob(safeBase64)));
        const parsedData = JSON.parse(decodedString);

        if (parsedData.expiresAt && Date.now() > parsedData.expiresAt) {
          setIsExpired(true);
          setIsLoading(false);
          return;
        }

        setPayloadData(parsedData);
        const { vehicleId, startTime, endTime } = parsedData;
        const token = parsedData.token || parsedData.apiKey || parsedData.serviceToken || '';
        const companyId = parsedData.companyId || '';
        const companyIds = parsedData.companyIds || '';

        console.log('Share Link Parsed Data:', parsedData);
        console.log('Condition check:', { vehicleId, startTime, endTime, token_exists: !!token });

        if (vehicleId && startTime && endTime) {
          const retaseRes = await fetchRetasePublic(token, vehicleId, companyId, startTime, endTime, tz);
          const apiDataRaw = retaseRes?.data?.retase ?? [];

          const uniqueTripsMap = new Map();
          apiDataRaw.forEach((tripData) => {
            const routes = tripData.routes || [];
            const details = tripData.details || [];

            const spatialNames = routes.map((v) => v.spatial_name || v.spatialName).filter(Boolean);
            const routeTitle = spatialNames.length > 0 ? spatialNames.join(' - ') : `Route ID: ${tripData.route_trip_id}`;

            let earliestStart = null;
            let latestEnd = null;
            details.forEach((d) => {
              const st = dayjs(d.start);
              const en = dayjs(d.end);
              if (!earliestStart || st.isBefore(earliestStart)) earliestStart = st;
              if (!latestEnd || en.isAfter(latestEnd)) latestEnd = en;
            });

            const finalStart = earliestStart ? earliestStart.format('YYYY-MM-DD HH:mm:ss') : 'no-start';
            const finalEnd = latestEnd ? latestEnd.format('YYYY-MM-DD HH:mm:ss') : 'no-end';

            const uniqueKey = `${routeTitle}_${finalStart}_${finalEnd}`;
            if (!uniqueTripsMap.has(uniqueKey)) {
              uniqueTripsMap.set(uniqueKey, tripData);
            }
          });
          const apiData = Array.from(uniqueTripsMap.values());

          const processedTrips = [];

          apiData.forEach((tripData) => {
            const sortedRoutes = (tripData.routes || []).sort((a, b) => a.sequence_number - b.sequence_number);
            const detailsArray = tripData.details || [];
            detailsArray.forEach((dt) => {
              processedTrips.push({
                route_trip_id: tripData.route_trip_id,
                total_trips: tripData.total_trips || 1,
                routes: sortedRoutes,
                start_time: dt.start || null,
                end_time: dt.end || null,
                duration: dt.dur || 0,
                license_plate: parsedData.fleet_name || 'Unknown'
              });
            });
          });
          processedTrips.sort((a, b) => new Date(a.start_time || 0).getTime() - new Date(b.start_time || 0).getTime());
          setTripList(processedTrips);

          const wps = await fetchAllWaypointsPublic(token, vehicleId, companyId, startTime, endTime, tz);
          setAllWaypointsRaw(wps);

          const locs = await fetchAllLocationsPublic(token, companyIds);
          setPolygonLocations(locs);
        } else {
          throw new Error('Metadata URL tidak lengkap.');
        }
        setIsLoading(false);
      } catch (err) {
        console.error(err);
        setErrorMsg('Data link rusak atau gagal mengambil data dari server.');
        setIsLoading(false);
      }
    };
    initializeData();
  }, [location]);

  useEffect(() => {
    if (!allWaypointsRaw || allWaypointsRaw.length === 0) return;
    if (!activePlayId) {
      const sortedAll = [...allWaypointsRaw].sort((a, b) => getEpochFromWp(a) - getEpochFromWp(b));
      setActiveWaypoints(enrichWaypointsWithSensorData(sortedAll, tripList));
    } else {
      const filteredWp = allWaypointsRaw.filter((wp) => {
        const wpEpoch = getEpochFromWp(wp);
        return wpEpoch >= activeTimeRange.start - 300000 && wpEpoch <= activeTimeRange.end + 300000;
      });
      const sortedFiltered = [...filteredWp].sort((a, b) => getEpochFromWp(a) - getEpochFromWp(b));
      setActiveWaypoints(enrichWaypointsWithSensorData(sortedFiltered, tripList));
    }
  }, [allWaypointsRaw, activePlayId, activeTimeRange, tripList]);

  const { path: derivedPath, speeds: derivedSpeeds } = useMemo(() => {
    if (activeWaypoints.length === 0) return { path: [], speeds: [] };
    const vehicleTarget = { id: payloadData?.vehicleId, max_speed: 70 };
    const generated = generatePathAndSpeeds
      ? generatePathAndSpeeds(activeWaypoints, vehicleTarget, { maxPoints: 500000 })
      : { path: [], speeds: [] };
    return { path: generated.path || [], speeds: generated.speeds || [] };
  }, [activeWaypoints, payloadData]);

  const derivedPathRef = useRef(derivedPath);
  const derivedSpeedsRef = useRef(derivedSpeeds);
  const activeWaypointsRef = useRef(activeWaypoints);

  useEffect(() => {
    derivedPathRef.current = derivedPath;
    derivedSpeedsRef.current = derivedSpeeds;
    activeWaypointsRef.current = activeWaypoints;
  }, [derivedPath, derivedSpeeds, activeWaypoints]);

  const maxIndex = useMemo(() => Math.max(0, derivedPath.length - 1), [derivedPath.length]);

  useEffect(() => {
    if (isLoading || errorMsg || isExpired || !mapContainerRef.current) return;
    if (!mapInstanceRef.current) {
      let styleTag = document.getElementById('glow-blink-style');
      if (!styleTag) {
        styleTag = document.createElement('style');
        styleTag.id = 'glow-blink-style';
        styleTag.innerHTML = `
          .polygon-glow-active {
            stroke: var(--glow-color, #ffffff) !important;
            stroke-width: 4px !important;
            stroke-dasharray: 6 !important;
            animation: polygon-march 1s linear infinite;
            filter: drop-shadow(0px 0px 4px var(--glow-color, #ffffff)) brightness(1.5) !important;
          }
          @keyframes polygon-march {
            to { stroke-dashoffset: -12; }
          }
        `;
        document.head.appendChild(styleTag);
      }

      const map = L.map(mapContainerRef.current, {
        center: [-2.5489, 118.0148],
        zoom: 5,
        zoomControl: true,
        attributionControl: false
      });
      const defaultLayer = baseMaps['Satelite Map'] || Object.values(baseMaps)[0];
      defaultLayer.addTo(map);

      tripLayerRef.current = L.featureGroup().addTo(map);
      locationLayerRef.current = L.featureGroup().addTo(map);

      map.on('dragstart', () => {
        setLocPopoverOpen(false);
      });

      mapInstanceRef.current = map;
      setIsMapReady(true);
      setTimeout(() => map.invalidateSize(), 500);
    }
  }, [isLoading, errorMsg, isExpired]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const locLayer = locationLayerRef.current;
    if (!map || !locLayer || polygonLocations.length === 0) return;

    locLayer.clearLayers();

    polygonLocations.forEach((loc) => {
      if (loc.geom) {
        try {
          const wkt = loc.geom;
          const coordsStr = wkt.replace('POLYGON((', '').replace('))', '').replace('POLYGON ((', '');
          const pairs = coordsStr.split(',');
          const polyCoords = pairs.map((pair) => {
            const [lng, ptLat] = pair.trim().split(' ');
            return [Number(ptLat), Number(lng)];
          });

          if (polyCoords.length > 0) {
            const typeColor = loc.location_type === 'PIT' ? '#1565c0' : loc.location_type === 'Jetty' ? '#2e7d32' : '#7b1fa2';
            const polygon = L.polygon(polyCoords, { color: typeColor, weight: 2, fillOpacity: 0.15, data: loc });

            polygon.on('click', (e) => {
              L.DomEvent.stopPropagation(e);
              setActiveLocationPolygon({ layer: polygon, data: loc });
              setLocPopoverOpen(true);
            });
            locLayer.addLayer(polygon);
          }
        } catch (e) {
          console.warn('WKT Parse error', e);
        }
      }
    });
  }, [polygonLocations]);

  useEffect(() => {
    if (locPopoverOpen && activeLocationPolygon?.layer?._path) {
      const color = activeLocationPolygon.layer.options?.color || '#ffffff';
      activeLocationPolygon.layer._path.style.setProperty('--glow-color', color);
      activeLocationPolygon.layer._path.classList.add('polygon-glow-active');
    } else {
      if (activeLocationPolygon?.layer?._path) {
        activeLocationPolygon.layer._path.classList.remove('polygon-glow-active');
        activeLocationPolygon.layer._path.style.removeProperty('--glow-color');
      }
    }
  }, [activeLocationPolygon, locPopoverOpen]);

  const triggerPopup = useCallback((marker, content, isPersistent = false) => {
    if (!marker || !marker._map) return;
    let hasLayer = false;
    let visibleParent = null;

    if (historyClustersRef.current) {
      if (historyClustersRef.current.over?.hasLayer(marker)) {
        hasLayer = true;
        visibleParent = historyClustersRef.current.over.getVisibleParent(marker);
      } else if (historyClustersRef.current.idle?.hasLayer(marker)) {
        hasLayer = true;
        visibleParent = historyClustersRef.current.idle.getVisibleParent(marker);
      } else if (historyClustersRef.current.off?.hasLayer(marker)) {
        hasLayer = true;
        visibleParent = historyClustersRef.current.off.getVisibleParent(marker);
      }
    }

    if (hasLayer && visibleParent && visibleParent !== marker) {
      return;
    }
    if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current);
    try {
      if (content) {
        marker.bindPopup(content).openPopup();
      } else {
        if (marker.getPopup && marker.getPopup()) marker.openPopup();
      }
      if (!isPersistent) {
        popupTimeoutRef.current = setTimeout(() => {
          if (marker._map) marker.closePopup();
          popupTimeoutRef.current = null;
        }, 2000);
      }
    } catch (error) {
      console.warn('Marker failed to open popup:', error);
    }
  }, []);

  const checkPolygonIntersection = useCallback((currentLoc, currentData) => {
    if (!locationLayerRef.current || !currentLoc || !currentData) return;
    let foundArea = null;

    locationLayerRef.current.eachLayer((layer) => {
      if (layer instanceof L.Polygon && isPointInPolygon(currentLoc, layer)) {
        foundArea = layer.options.data;
      }
    });

    if (foundArea) {
      const areaId = foundArea.id || foundArea.pool_name;
      if (currentAreaIdRef.current !== areaId) {
        currentAreaIdRef.current = areaId;
        const name = foundArea.name || foundArea.pool_name || foundArea.geofence_name || 'Area';
        const type = foundArea.location_type || 'Lokasi';
        const timeStr = formatDisplayTime(currentData.start_wp_time || currentData.start_time);
        if (tripMarkerRef.current) {
          tripMarkerRef.current.bindPopup(`<b>Truk masuk ke ${name} (${type})</b><br/>Jam: ${timeStr}`).openPopup();
        }
      }
    } else {
      currentAreaIdRef.current = null;
    }
  }, []);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !tripLayerRef.current || activeWaypoints.length === 0 || derivedPath.length === 0) return;

    tripLayerRef.current.clearLayers();
    animIndexRef.current = 0;
    setCurrentIndexState(0);
    lastIndexRef.current = -1;
    currentAreaIdRef.current = null;
    updatePlaying(false);

    const createClusterIcon = (bgColor) => {
      return function (cluster) {
        return new L.DivIcon({
          html: `<div style="background-color: ${bgColor}; color: white; border-radius: 50%; border: 2px solid white; width: 35px; height: 35px; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 14px; box-shadow: 0 4px 8px rgba(0,0,0,0.3);"><span>${cluster.getChildCount()}</span></div>`,
          className: 'custom-history-cluster-icon',
          iconSize: new L.Point(35, 35)
        });
      };
    };

    historyClustersRef.current = {
      over: L.markerClusterGroup({
        chunkedLoading: true,
        maxClusterRadius: 40,
        iconCreateFunction: createClusterIcon('#F44336')
      }),
      idle: L.markerClusterGroup({
        chunkedLoading: true,
        maxClusterRadius: 40,
        iconCreateFunction: createClusterIcon('#FFC107')
      }),
      off: L.markerClusterGroup({
        chunkedLoading: true,
        maxClusterRadius: 40,
        iconCreateFunction: createClusterIcon('#464646')
      })
    };

    const idleMarkerIcon = L.icon({ iconUrl: EngineIdleMarker, iconSize: [24, 24], iconAnchor: [12, 12], popupAnchor: [0, -12] });
    const overMarkerIcon = L.icon({ iconUrl: EngineOverMarker, iconSize: [24, 24], iconAnchor: [12, 12], popupAnchor: [0, -12] });
    const offMarkerIcon = L.icon({ iconUrl: EngineOffMarker, iconSize: [24, 24], iconAnchor: [12, 12], popupAnchor: [0, -12] });

    let previousValidLatLng = null;
    let lastMarkerType = 'NORMAL';
    let consecutiveMarkerCount = 0;
    eventMarkersRef.current = {};

    activeWaypoints.forEach((wp, index) => {
      const lat = wp.lat ?? wp.latitude;
      const lng = wp.lon ?? wp.lng ?? wp.longitude;

      if (lat != null && lng != null) {
        const currentLatLng = [Number(lat), Number(lng)];
        const engineStatus = String(wp.engine || wp.engine_status || wp.info?.engine_status || '').toUpperCase();
        const currentSpeed = Number(wp.speed || 0);
        const limitSpeed = 70;

        const spatialId = Number(wp.spatial_id || wp.info?.spatial_id || 0);
        const spatialMaxSpeed = Number(wp.spatial_max_speed || wp.info?.spatial_max_speed || 0);

        const spatialMinSpeed = Number(wp.spatial_min_speed || wp.info?.spatial_min_speed || 0);

        let color = wp.lineColor || '#1d89d5';
        let currentEventType = wp._computedSensorType || 'NORMAL';

        if (wp.harsh_event || wp.info?.harsh_event) {
          color = '#F44336';
          currentEventType = 'HARSH_EVENT';
        } else if (wp.is_in_retase !== false && spatialId > 0 && spatialMaxSpeed > 0 && currentSpeed > spatialMaxSpeed) {
          color = '#F39399';
          currentEventType = 'POLYGON_OVERSPEED';
        } else if (spatialId > 0 && spatialMinSpeed > 0 && currentSpeed > 0 && currentSpeed < spatialMinSpeed) {
          color = '#FFC107';
          currentEventType = 'POLYGON_UNDERSPEED';
        } else if (engineStatus === 'OFF') {
          color = '#464646';
          currentEventType = 'OFF';
        } else if (engineStatus === 'IDLE') {
          color = '#FFC107';
          currentEventType = 'IDLE';
        } else if (engineStatus === 'ON' && currentSpeed > limitSpeed) {
          color = '#F44336';
          currentEventType = 'OVERSPEED';
        }

        if (previousValidLatLng) {
          L.polyline([previousValidLatLng, currentLatLng], { color: color, weight: 5, opacity: 0.9 }).addTo(tripLayerRef.current);
        }

        let shouldDrawMarker = false;
        if (currentEventType !== 'NORMAL') {
          if (currentEventType !== lastMarkerType) {
            shouldDrawMarker = true;
            lastMarkerType = currentEventType;
            consecutiveMarkerCount = 1;
          } else {
            consecutiveMarkerCount++;
            if (consecutiveMarkerCount >= 9) {
              shouldDrawMarker = true;
              consecutiveMarkerCount = 1;
            }
          }
        } else {
          lastMarkerType = 'NORMAL';
          consecutiveMarkerCount = 0;
        }

        if (shouldDrawMarker) {
          const displayTime = formatDisplayTime(wp.start_wp_time || wp.start_time || '');
          let marker = null;

          if (currentEventType === 'HARSH_EVENT')
            marker = L.marker(currentLatLng, { icon: overMarkerIcon, eventType: 'HARSH_EVENT' }).bindPopup(
              `<b><span style="color: #F44336;">⚠ ${String(wp.harsh_event).toUpperCase().replace('BREAKING', 'BRAKING')}</span></b><br/>Speed: <b>${currentSpeed} km/h</b><br/>Time: ${displayTime}`
            );
          else if (currentEventType === 'OFF')
            marker = L.marker(currentLatLng, { icon: offMarkerIcon, eventType: 'OFF' }).bindPopup(
              `<b>Engine OFF</b><br/>Time: ${displayTime}`
            );
          else if (currentEventType === 'IDLE')
            marker = L.marker(currentLatLng, { icon: idleMarkerIcon, eventType: 'IDLE' }).bindPopup(
              `<b>Engine IDLE</b><br/>Time: ${displayTime}`
            );
          else if (currentEventType === 'POLYGON_OVERSPEED') {
            const polygonTypeName = wp.polygon_type_name || wp.info?.polygon_type_name || wp.spatial_description || 'Geofence';
            const sMax = Number(wp.spatial_max_speed || wp.info?.spatial_max_speed || 0);
            marker = L.marker(currentLatLng, { icon: overMarkerIcon, eventType: 'OVERSPEED' }).bindPopup(
              `<b><span style="color: #F39399;">⚠ OVER SPEED (${polygonTypeName})</span></b><br/>` +
                `Speed: <b>${currentSpeed} km/h</b> (Max: ${sMax} km/h)<br/>` +
                `Time: ${displayTime}`
            );
          } else if (currentEventType === 'POLYGON_UNDERSPEED') {
            const polygonTypeName = wp.polygon_type_name || wp.info?.polygon_type_name || wp.spatial_description || 'Geofence';
            const sMin = Number(wp.spatial_min_speed || wp.info?.spatial_min_speed || 0);
            const licensePlate = payloadData?.fleet_name || payloadData?.vehicle_license_plate || payloadData?.license_plate || 'Unknown';
            marker = L.marker(currentLatLng, { icon: idleMarkerIcon, eventType: 'UNDERSPEED' }).bindPopup(
              `<b><span style="color: #FFC107;">⚠ ${licensePlate} Under speed ${currentSpeed} in location ${polygonTypeName}, Spatial Min speed : ${sMin}</span></b><br/>Time: ${displayTime}`
            );
          } else if (currentEventType === 'OVERSPEED')
            marker = L.marker(currentLatLng, { icon: overMarkerIcon, eventType: 'OVERSPEED' }).bindPopup(
              `<b><span style="color: #F44336;">⚠ OVER SPEED</span></b><br/>Speed: <b>${currentSpeed} km/h</b><br/>Time: ${displayTime}`
            );
          else if (currentEventType.startsWith('DR') || currentEventType.startsWith('DP') || currentEventType === 'DUM') {
            marker = L.circleMarker(currentLatLng, { radius: 5, color: '#fff', weight: 1, fillColor: color, fillOpacity: 1 }).bindPopup(
              `<b>Sensor: ${wp.sensorLabel}</b><br/>Time: ${displayTime}`
            );
          }

          if (marker) {
            if (['HARSH_EVENT', 'POLYGON_OVERSPEED', 'OVERSPEED'].includes(currentEventType)) {
              historyClustersRef.current.over.addLayer(marker);
            } else if (['IDLE', 'POLYGON_UNDERSPEED'].includes(currentEventType)) {
              historyClustersRef.current.idle.addLayer(marker);
            } else {
              historyClustersRef.current.off.addLayer(marker);
            }
            const derivedIdx = Math.round((index / (activeWaypoints.length - 1)) * (derivedPath.length - 1));
            eventMarkersRef.current[derivedIdx] = marker;
          }
        }
        previousValidLatLng = currentLatLng;
      }
    });

    tripLayerRef.current.addLayer(historyClustersRef.current.over);
    tripLayerRef.current.addLayer(historyClustersRef.current.idle);
    tripLayerRef.current.addLayer(historyClustersRef.current.off);

    const start = derivedPath[0];
    const end = derivedPath[derivedPath.length - 1];

    startMarkerRef.current = L.circleMarker(start, { radius: 6, color: '#fff', weight: 2, fillColor: 'green', fillOpacity: 1 })
      .addTo(tripLayerRef.current)
      .bindPopup('Start');
    endMarkerRef.current = L.circleMarker(end, { radius: 6, color: '#fff', weight: 2, fillColor: 'red', fillOpacity: 1 })
      .addTo(tripLayerRef.current)
      .bindPopup('End');

    const initialAngle = activeWaypoints[0]?.angle ?? activeWaypoints[0]?.direction ?? 0;
    tripMarkerRef.current = L.marker(start, { icon: getRotatedTruckIcon(initialAngle), zIndexOffset: 2000 }).addTo(tripLayerRef.current);

    const bounds = L.latLngBounds(derivedPath);
    const fitMapBounds = () => {
      if (map && map._container) {
        map.invalidateSize();
        const paddingRight = isSidebarOpenRef.current ? 320 : 20;
        map.fitBounds(bounds, { padding: [40, 40], paddingBottomRight: [paddingRight, 200] });
      }
    };

    fitMapBounds();
    const t1 = setTimeout(fitMapBounds, 100);
    const t2 = setTimeout(fitMapBounds, 500);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derivedPath, activeWaypoints, isMapReady]);

  const panToWithOffset = useCallback((lat, lon) => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const targetPoint = map.project(L.latLng(lat, lon), map.getZoom());
    targetPoint.y += 50;
    if (isSidebarOpenRef.current) {
      targetPoint.x += 150;
    }
    map.panTo(map.unproject(targetPoint, map.getZoom()), { animate: true, duration: 0.1, easeLinearity: 0.5 });
  }, []);

  const updateVehicleRotation = useCallback((currentIndex, pathArray, providedAngle = null, currentDataWp = null) => {
    if (!tripMarkerRef.current || !pathArray || pathArray.length === 0) return;
    const vehicleImg = document.getElementById('trip-vehicle-img');
    if (!vehicleImg) return;

    let styleTag = document.getElementById('glow-blink-style');
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = 'glow-blink-style';
      styleTag.innerHTML = `
        @keyframes pulseRingRed {
          0% { transform: scale(0.8); opacity: 0.8; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        @keyframes pulseRingYellow {
          0% { transform: scale(0.8); opacity: 0.8; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        .pulse-ring {
          position: absolute;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          z-index: 1;
          display: none;
        }
        .pulse-ring.glow-overspeed {
          display: block;
          background-color: rgba(244, 67, 54, 0.6);
          box-shadow: 0 0 10px rgba(244, 67, 54, 0.8);
          animation: pulseRingRed 1s infinite ease-out;
        }
        @keyframes pulseRingOrange {
          0% { transform: scale(0.8); opacity: 0.8; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        .pulse-ring.glow-overspeed-spatial {
          display: block;
          background-color: rgba(243, 147, 153, 0.6);
          box-shadow: 0 0 10px rgba(243, 147, 153, 0.8);
          animation: pulseRingOrange 1s infinite ease-out;
        }
        .pulse-ring.glow-idle {
          display: block;
          background-color: rgba(255, 193, 7, 0.6);
          box-shadow: 0 0 10px rgba(255, 193, 7, 0.8);
          animation: pulseRingYellow 1.5s infinite ease-out;
        }
      `;
      document.head.appendChild(styleTag);
    }

    let glowClass = '';
    if (currentDataWp) {
      const spatialId = Number(currentDataWp.spatial_id || currentDataWp.info?.spatial_id || 0);
      const spatialMaxSpeed = Number(currentDataWp.spatial_max_speed || currentDataWp.info?.spatial_max_speed || 0);
      const spatialMinSpeed = Number(currentDataWp.spatial_min_speed || currentDataWp.info?.spatial_min_speed || 0);
      const engineStatus = String(
        currentDataWp.engine || currentDataWp.engine_status || currentDataWp.info?.engine_status || ''
      ).toUpperCase();
      const currentSpeed = Number(currentDataWp.speed || 0);
      const limitSpeed = Number(currentDataWp.vehicle?.max_speed || 70);

      const status = String(currentDataWp.status || currentDataWp.info?.status_text || '').toLowerCase();
      const isAtPolygon =
        status.includes('at pool') || status.includes('at geofence') || status.includes('at customer') || status.includes('at polygon');
      const isAtTrip = status === 'at trip';
      const isStatusOverSpeed = status.includes('over speed');
      const isStatusUnderSpeed = status.includes('under speed');
      const isOverSpeedSpatial =
        currentDataWp.is_in_retase !== false && spatialId > 0 && spatialMaxSpeed > 0 && currentSpeed > spatialMaxSpeed;
      const isUnderSpeedSpatial = spatialId > 0 && spatialMinSpeed > 0 && currentSpeed > 0 && currentSpeed < spatialMinSpeed;

      const isMoving =
        engineStatus === 'ON' &&
        currentSpeed > 0 &&
        (isAtTrip || isAtPolygon || (isOverSpeedSpatial && isStatusOverSpeed) || (isUnderSpeedSpatial && isStatusUnderSpeed)) &&
        !status.includes('delay');

      if (isMoving) {
        if (currentDataWp.harsh_event || currentDataWp.info?.harsh_event) {
          glowClass = 'glow-overspeed';
        } else if (currentDataWp.is_in_retase !== false && spatialId > 0 && spatialMaxSpeed > 0 && currentSpeed > spatialMaxSpeed) {
          glowClass = 'glow-overspeed-spatial';
        } else if (spatialId > 0 && spatialMinSpeed > 0 && currentSpeed > 0 && currentSpeed < spatialMinSpeed) {
          glowClass = 'glow-idle';
        } else if (engineStatus === 'ON' && currentSpeed > limitSpeed) {
          glowClass = 'glow-overspeed';
        } else if (engineStatus === 'IDLE') {
          glowClass = 'glow-idle';
        }
      }
    }

    const pulseRing = document.getElementById('trip-vehicle-pulse');
    if (pulseRing && pulseRing.className !== `pulse-ring ${glowClass}`) {
      pulseRing.className = `pulse-ring ${glowClass}`;
    }

    if (providedAngle !== null && providedAngle !== undefined && providedAngle !== '') {
      vehicleImg.style.transform = `rotate(${Number(providedAngle) - 90}deg)`;
      return;
    }
    const currentLoc = pathArray[Math.floor(currentIndex)];
    const isAtEnd = Math.floor(currentIndex) >= pathArray.length - 1;
    let refLoc1 = currentLoc;
    let refLoc2 = isAtEnd ? currentLoc : pathArray[Math.floor(currentIndex) + 1];

    if (isAtEnd) refLoc1 = pathArray[Math.floor(currentIndex) - 1] || currentLoc;

    if (refLoc1 && refLoc2 && (refLoc1[0] !== refLoc2[0] || refLoc1[1] !== refLoc2[1])) {
      let heading = calculateHeading(refLoc1[0], refLoc1[1], refLoc2[0], refLoc2[1]);
      vehicleImg.style.transform = `rotate(${heading - 90}deg)`;
    }
  }, []);

  const updateGaugeData = useCallback((wpIndexRaw) => {
    const currentWps = activeWaypointsRef.current;
    if (!currentWps || currentWps.length === 0) return;
    const safeWpIndex = Math.min(Math.max(0, Math.round(wpIndexRaw)), currentWps.length - 1);
    const currentDataWp = currentWps[safeWpIndex];

    if (currentDataWp) {
      setCurrentPointSpeed(Math.round(currentDataWp.speed || currentDataWp.canbus?.canVehicleSpeed || 0));
      setCurrentRPM(currentDataWp.canbus?.canEngineRPM || 0);
      setCurrentFuel({
        litre: currentDataWp.can_fuel_litre || 0,
        percent: currentDataWp.can_fuel_percentage || 0,
        cap: currentDataWp.fuel_tank || 100
      });
    }
  }, []);

  const handlePlayPause = useCallback(() => {
    updatePlaying(!isPlayingRef.current);
  }, [updatePlaying]);

  const seekToIndex = useCallback(
    (index) => {
      if (!derivedPathRef.current.length) return;
      const maxDerivedIndex = derivedPathRef.current.length - 1;
      const i = Math.min(Math.max(0, Math.floor(index)), maxDerivedIndex);

      animIndexRef.current = i;
      setCurrentIndexState(i);

      const maxWpIndex = activeWaypointsRef.current.length - 1;
      const mappedWpIndex = (i / maxDerivedIndex) * maxWpIndex;
      updateGaugeData(mappedWpIndex);

      if (tripMarkerRef.current) {
        const p = derivedPathRef.current[i];
        tripMarkerRef.current.setLatLng(p);
        mapInstanceRef.current.panTo(p, { animate: true });

        const currentDataWp = activeWaypointsRef.current[Math.round(mappedWpIndex)];
        updateVehicleRotation(i, derivedPathRef.current, currentDataWp?.angle ?? currentDataWp?.direction ?? null, currentDataWp);

        checkPolygonIntersection(p, currentDataWp);

        if (i === 0 && startMarkerRef.current) triggerPopup(startMarkerRef.current, null, true);
        else if (i === maxDerivedIndex && endMarkerRef.current) triggerPopup(endMarkerRef.current, null, true);
        else {
          const eventMarker = eventMarkersRef.current[i];
          if (eventMarker) {
            const popupContent = eventMarker.getPopup() ? eventMarker.getPopup().getContent() : null;
            if (popupContent) {
              triggerPopup(tripMarkerRef.current, popupContent);
            } else {
              triggerPopup(eventMarker);
            }
          }
        }
      }
    },
    [updateVehicleRotation, updateGaugeData, triggerPopup, checkPolygonIntersection]
  );

  const handleSeekToPercent = useCallback(
    (pct) => {
      if (activeWaypoints.length === 0) return;
      const maxIndex = activeWaypoints.length - 1;
      const targetIndex = Math.round((pct / 100) * maxIndex);
      seekToIndex(targetIndex);
    },
    [activeWaypoints, seekToIndex]
  );

  useEffect(() => {
    if (!mapInstanceRef.current || !tripMarkerRef.current || activeWaypoints.length === 0) return;
    let rafId = null;
    let lastPanTime = performance.now();
    let lastStateUpdate = 0;

    const step = (time) => {
      if (!isPlayingRef.current) return;
      const dt = (time - lastTimeRef.current) / 1000;
      lastTimeRef.current = time;

      const maxIdx = derivedPathRef.current.length - 1;
      const i = Math.floor(animIndexRef.current);
      const nowMs = performance.now();

      if (nowMs - lastStateUpdate > 100) {
        lastStateUpdate = nowMs;
        const mappedWpIndex = (i / maxIdx) * (activeWaypointsRef.current.length - 1);
        updateGaugeData(mappedWpIndex);
        setCurrentIndexState((prev) => (prev !== i ? i : prev));
      }

      if (i !== lastIndexRef.current) {
        const previousIndex = lastIndexRef.current;
        lastIndexRef.current = i;

        if (i === 0 && startMarkerRef.current) triggerPopup(startMarkerRef.current, null, true);
        else if (i === maxIdx && endMarkerRef.current) triggerPopup(endMarkerRef.current, null, true);
        else {
          if (previousIndex === 0 && startMarkerRef.current) triggerPopup(startMarkerRef.current, null, false);
          else if (previousIndex === maxIdx && endMarkerRef.current) triggerPopup(endMarkerRef.current, null, false);
          const eventMarker = eventMarkersRef.current[i];
          if (eventMarker) {
            const popupContent = eventMarker.getPopup() ? eventMarker.getPopup().getContent() : null;
            if (popupContent) {
              triggerPopup(tripMarkerRef.current, popupContent);
            } else {
              triggerPopup(eventMarker);
            }
          }
        }
      }

      const speedFactor = Math.max(0.1, Number(derivedSpeedsRef.current[i] ?? 36));
      const delta = Math.max(0.02 * dt * 60, (speedFactor / 50) * dt) * playbackSpeedRef.current;
      let nextIndex = animIndexRef.current + delta;

      if (nextIndex < 0) {
        nextIndex = 0;
        updatePlaying(false);
      } else if (nextIndex > maxIdx) {
        nextIndex = maxIdx;
        updatePlaying(false);
      }
      animIndexRef.current = nextIndex;

      const floorIdx = Math.floor(animIndexRef.current);
      const ceilIdx = Math.min(maxIdx, Math.ceil(animIndexRef.current));
      const fraction = animIndexRef.current - floorIdx;

      const locA = derivedPathRef.current[floorIdx];
      const locB = derivedPathRef.current[ceilIdx];

      let newLoc = locA;
      if (locA && locB && floorIdx !== ceilIdx) {
        newLoc = [locA[0] + (locB[0] - locA[0]) * fraction, locA[1] + (locB[1] - locA[1]) * fraction];
      }

      if (newLoc) {
        tripMarkerRef.current.setLatLng(newLoc);
        const mappedWpIndex = Math.round((animIndexRef.current / maxIdx) * (activeWaypointsRef.current.length - 1));
        const currentDataWp = activeWaypointsRef.current[mappedWpIndex];

        updateVehicleRotation(
          animIndexRef.current,
          derivedPathRef.current,
          currentDataWp?.angle ?? currentDataWp?.direction ?? null,
          currentDataWp
        );
        checkPolygonIntersection(newLoc, currentDataWp);

        if (nowMs - lastPanTime > 30) {
          lastPanTime = nowMs;
          panToWithOffset(newLoc[0], newLoc[1], false);
        }
      }
      if (isPlayingRef.current) rafId = requestAnimationFrame(step);
    };

    if (isPlaying) rafId = requestAnimationFrame(step);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [
    isPlaying,
    activeWaypoints.length,
    panToWithOffset,
    updatePlaying,
    updateVehicleRotation,
    updateGaugeData,
    triggerPopup,
    checkPolygonIntersection
  ]);

  const handleTogglePlayTrip = (trip, index) => {
    const tripId = `trip-${index}`;
    if (activePlayId === tripId) {
      setActivePlayId(null);
      setActiveTimeRange({ start: 0, end: 0 });
    } else {
      setActivePlayId(tripId);
      setActiveTimeRange({ start: new Date(trip.start_time).getTime(), end: new Date(trip.end_time).getTime() });
    }
  };

  if (isLoading)
    return (
      <Box sx={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  if (isExpired)
    return (
      <Box sx={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f1f5f9' }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3, maxWidth: 400, textAlign: 'center' }}>
          <ErrorOutlineIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" fontWeight="bold">
            Link Kedaluwarsa
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Tautan ini sudah melewati batas waktu akses.
          </Typography>
          <Button variant="contained" sx={{ bgcolor: '#485D9A' }} onClick={() => navigate('/login')}>
            Home
          </Button>
        </Paper>
      </Box>
    );
  if (errorMsg)
    return (
      <Box sx={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="error">{errorMsg}</Typography>
      </Box>
    );

  return (
    <Box ref={wrapperRef} sx={{ width: '100vw', height: '100vh', position: 'relative', overflow: 'hidden' }}>
      <Box
        sx={{
          position: 'absolute',
          top: 14,
          right: isSidebarOpen ? 345 : 50,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 1
        }}
      >
        <Paper elevation={3} sx={{ p: 0.5, borderRadius: 1, display: 'flex', gap: 0.5 }}>
          <IconButton onClick={() => setIsLayerMenuOpen(!isLayerMenuOpen)} size="small" sx={{ color: '#485D9A' }}>
            <LayersIcon />
          </IconButton>
          <IconButton size="small" onClick={toggleFullscreen}>
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Paper>
        {isLayerMenuOpen && (
          <Paper elevation={4} sx={{ width: 160, borderRadius: 2, overflow: 'hidden' }}>
            <List disablePadding>
              {Object.keys(baseMaps).map((mapName) => (
                <ListItem
                  key={mapName}
                  button
                  onClick={() => {
                    if (mapInstanceRef.current) {
                      Object.values(baseMaps).forEach((l) => mapInstanceRef.current.hasLayer(l) && mapInstanceRef.current.removeLayer(l));
                      baseMaps[mapName].addTo(mapInstanceRef.current);
                      setActiveBaseMap(mapName);
                      setIsLayerMenuOpen(false);
                    }
                  }}
                  sx={{
                    bgcolor: mapName === activeBaseMap ? 'rgba(72, 93, 154, 0.15)' : 'transparent',
                    borderLeft: mapName === activeBaseMap ? '4px solid #485D9A' : '4px solid transparent'
                  }}
                >
                  <ListItemText
                    primary={mapName}
                    primaryTypographyProps={{ fontSize: '0.8rem', color: mapName === activeBaseMap ? '#485D9A' : '#333' }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Box>

      {locPopoverOpen && (
        <Box
          sx={{
            position: 'absolute',
            top: 24,
            left: isSidebarOpen ? '40%' : '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid rgba(255,255,255,0.5)',
            p: 2,
            minWidth: 320,
            display: 'flex',
            flexDirection: 'column',
            pt: 3
          }}
        >
          <IconButton size="small" onClick={() => setLocPopoverOpen(false)} sx={{ position: 'absolute', top: 4, right: 4 }}>
            <CloseIcon fontSize="small" />
          </IconButton>

          <Typography variant="subtitle1" fontWeight="bold" color="#485D9A" pr={2}>
            {activeLocationPolygon?.data?.name || activeLocationPolygon?.data?.pool_name || activeLocationPolygon?.data?.geofence_name}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Company:{' '}
            {activeLocationPolygon?.data?.join?.company_name ||
              activeLocationPolygon?.data?.company?.name ||
              activeLocationPolygon?.data?.company ||
              '-'}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
            Tipe:{' '}
            {activeLocationPolygon?.data?.location_type === 'Geofence library' ? 'Geofence' : activeLocationPolygon?.data?.location_type}
          </Typography>
          {(activeLocationPolygon?.data?.max_speed > 0 || activeLocationPolygon?.data?.min_speed > 0) && (
            <Stack direction="row" alignItems="center" spacing={2} mb={2}>
              {activeLocationPolygon?.data?.max_speed > 0 && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Tooltip title="Max Speed">
                    <SpeedIcon fontSize="small" sx={{ color: 'error.main' }} />
                  </Tooltip>
                  <Typography variant="caption" color="error.main" fontWeight="bold">
                    {activeLocationPolygon.data.max_speed} Km/H
                  </Typography>
                </Stack>
              )}
              {activeLocationPolygon?.data?.min_speed > 0 && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Tooltip title="Min Speed">
                    <SpeedIcon fontSize="small" sx={{ color: 'info.main' }} />
                  </Tooltip>
                  <Typography variant="caption" color="info.main" fontWeight="bold">
                    {activeLocationPolygon.data.min_speed} Km/H
                  </Typography>
                </Stack>
              )}
            </Stack>
          )}
        </Box>
      )}

      <Box sx={{ position: 'absolute', top: 0, right: 0, height: '100%', zIndex: 999 }}>
        <MapSidebar isOpen={isSidebarOpen} onToggle={() => setIsSidebarOpen(!isSidebarOpen)}>
          <TripHistoryListForShare
            tripList={tripList}
            activePlayId={activePlayId}
            onTogglePlay={handleTogglePlayTrip}
            fleetName={payloadData?.fleet_name}
            startTime={payloadData?.startTime}
            endTime={payloadData?.endTime}
            token={payloadData?.token || payloadData?.apiKey || payloadData?.serviceToken || ''}
            companyId={payloadData?.companyId || ''}
          />
        </MapSidebar>
      </Box>

      <Box ref={mapContainerRef} sx={{ width: '100%', height: '100%', zIndex: 1 }} />

      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: isSidebarOpen ? 'calc(100% - 300px)' : '100%',
          zIndex: 998,
          transition: 'width 0.3s ease-in-out'
        }}
      >
        {payloadData && activeWaypoints.length > 0 && (
          <BottomPopoverTripHistoryForShare
            open={isPopoverOpen}
            onClose={() => setIsPopoverOpen(false)}
            isPlaying={isPlaying}
            onPlayPause={handlePlayPause}
            onNext={() => {
              seekToIndex(Math.min(maxIndex, animIndexRef.current + 1));
              updatePlaying(false);
            }}
            onPrev={() => {
              seekToIndex(Math.max(0, animIndexRef.current - 1));
              updatePlaying(false);
            }}
            onSeekToPercent={handleSeekToPercent}
            allWaypoints={activeWaypoints}
            onSeekToIndex={(index) => seekToIndex(index)}
            currentIndexState={Math.round((currentIndexState / maxIndex) * (activeWaypoints.length - 1)) || 0}
            currentPointSpeed={currentPointSpeed}
            currentRPM={currentRPM}
            currentFuel={currentFuel}
            currentProgressColor={
              activeWaypoints[Math.round((currentIndexState / maxIndex) * (activeWaypoints.length - 1))]?.lineColor || '#1d89d5'
            }
            playbackSpeed={playbackSpeed}
            onChangeSpeed={setPlaybackSpeed}
          />
        )}
      </Box>
    </Box>
  );
}
